/**
 * WebAPI Client for Browser-based API communication
 * 
 * CPI Architecture Pattern:
 * - All persistent state operations (inventory, buddies, igloos) go through REST
 * - Authentication tokens obtained via login/register
 * - Game Server uses separate sessionKey for real-time operations
 * - Client fetches inventory/buddies via REST (not socket)
 * 
 * Security Features:
 * - API Key authentication for initial requests
 * - JWT Bearer tokens for authenticated requests
 * - Automatic token refresh on 401
 * - Request retry with exponential backoff
 * - Response signature verification for tamper detection
 * - End-to-end encryption for sensitive real-time data
 */
export default class WebAPIClient {
    constructor(baseUrl = 'http://localhost:3002/api') {
        this.baseUrl = baseUrl;
        this.accessToken = null;
        this.refreshToken = null;
        this.sessionKey = null;
        this.profile = null;
        this.apiKey = null; // Optional API key for additional security
        this.signatureSecret = null; // For verifying signed responses
        
        // Request configuration
        this.timeout = 10000; // 10 second timeout
        this.maxRetries = 3;
        this.retryDelay = 1000; // Base delay for exponential backoff
        
        // Signature verification settings
        this.verifySignatures = true; // Enable signature verification
        this.signatureValidityMs = 5 * 60 * 1000; // 5 minutes
        
        // Encryption settings (CPI Architecture)
        this.encryptionEnabled = false;
        this.encryptionSessionId = null;
        this.aesKey = null; // AES key for session encryption
        this.aesKeyExpiry = null;
        
        // Load stored tokens on initialization
        this.loadStoredTokens();
    }
    
    /**
     * Set API key for requests (CPI-style client identification)
     * @param {string} apiKey 
     */
    setApiKey(apiKey) {
        this.apiKey = apiKey;
    }
    
    /**
     * Set signature secret for response verification
     * @param {string} secret 
     */
    setSignatureSecret(secret) {
        this.signatureSecret = secret;
    }
    
    /**
     * Enable or disable signature verification
     * @param {boolean} enabled 
     */
    setSignatureVerification(enabled) {
        this.verifySignatures = enabled;
    }
    
    /**
     * Load stored tokens from localStorage
     */
    loadStoredTokens() {
        try {
            const stored = localStorage.getItem('webapi_auth');
            if (stored) {
                const data = JSON.parse(stored);
                this.accessToken = data.accessToken;
                this.refreshToken = data.refreshToken;
                this.profile = data.profile;
            }
        } catch (error) {
            console.warn('Failed to load stored auth tokens:', error);
        }
    }
    
    /**
     * Store tokens to localStorage
     */
    storeTokens() {
        try {
            localStorage.setItem('webapi_auth', JSON.stringify({
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                profile: this.profile
            }));
        } catch (error) {
            console.warn('Failed to store auth tokens:', error);
        }
    }
    
    /**
     * Clear stored tokens
     */
    clearTokens() {
        this.accessToken = null;
        this.refreshToken = null;
        this.sessionKey = null;
        this.profile = null;
        localStorage.removeItem('webapi_auth');
    }
    
    /**
     * Make an authenticated HTTP request with retry logic
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Request options
     * @param {number} retryCount - Current retry attempt
     */
    async request(endpoint, options = {}, retryCount = 0) {
        const url = `${this.baseUrl}${endpoint}`;
        
        const headers = {
            'Content-Type': 'application/json',
            'bypass-tunnel-reminder': 'true', // Bypass localtunnel splash page
            ...options.headers
        };
        
        // Add API key if available (CPI-style)
        if (this.apiKey) {
            headers['X-API-Key'] = this.apiKey;
        }
        
        // Add auth header if we have a token
        if (this.accessToken && !options.skipAuth) {
            headers['Authorization'] = `Bearer ${this.accessToken}`;
        }
        
        const config = {
            method: options.method || 'GET',
            headers,
            ...options
        };
        
        if (options.body) {
            config.body = JSON.stringify(options.body);
        }
        
        // Add timeout using AbortController
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);
        config.signal = controller.signal;
        
        try {
            const response = await fetch(url, config);
            clearTimeout(timeoutId);
            
            const data = await response.json();
            
            if (!response.ok) {
                // Handle 401 - try to refresh token
                // IMPORTANT: Skip refresh for auth endpoints (login, register, refresh)
                // These endpoints are for obtaining new tokens, not using existing ones
                const isAuthEndpoint = endpoint.startsWith('/auth/login') || 
                                       endpoint.startsWith('/auth/register') ||
                                       endpoint.startsWith('/auth/refresh');
                
                if (response.status === 401 && this.refreshToken && !options.isRetry && !isAuthEndpoint) {
                    const refreshed = await this.refresh();
                    if (refreshed) {
                        return this.request(endpoint, { ...options, isRetry: true });
                    }
                }
                
                // Handle rate limiting with retry
                if (response.status === 429 && retryCount < this.maxRetries) {
                    const retryAfter = response.headers.get('Retry-After') || 
                        (this.retryDelay * Math.pow(2, retryCount));
                    await this.sleep(retryAfter);
                    return this.request(endpoint, options, retryCount + 1);
                }
                
                // Extract error message properly from various response formats
                let errorMessage = 'Request failed';
                if (data.message) {
                    errorMessage = data.message;
                } else if (data.error && typeof data.error === 'string') {
                    errorMessage = data.error;
                } else if (data.error && data.error.message) {
                    errorMessage = data.error.message;
                }
                
                throw {
                    status: response.status,
                    message: errorMessage,
                    code: data.code || (data.error && data.error.code) || 'UNKNOWN_ERROR'
                };
            }
            
            return data;
        } catch (error) {
            clearTimeout(timeoutId);
            
            // Handle timeout/network errors with retry
            if (error.name === 'AbortError' || error.code === 'NETWORK_ERROR') {
                if (retryCount < this.maxRetries) {
                    await this.sleep(this.retryDelay * Math.pow(2, retryCount));
                    return this.request(endpoint, options, retryCount + 1);
                }
            }
            
            if (error.status) throw error;
            
            throw {
                status: 0,
                message: error.message || 'Network error',
                code: 'NETWORK_ERROR'
            };
        }
    }
    
    /**
     * Sleep helper for retry delays
     * @param {number} ms - Milliseconds to sleep
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    // ==========================================
    // SIGNATURE VERIFICATION (CPI Security)
    // ==========================================
    
    /**
     * Verify a signed response from the WebAPI
     * CPI Architecture: All critical data is signed to prevent tampering
     * @param {Object} response - Response that may contain signed data
     * @returns {Object} { valid: boolean, data: any, error?: string }
     */
    verifySignedResponse(response) {
        // Check if response is a signed response format
        if (!response || typeof response !== 'object') {
            return { valid: true, data: response };
        }
        
        // Signed response format: { data, signature, goodUntil, userId?, sessionId? }
        if (!response.signature || !response.goodUntil) {
            // Not a signed response, return as-is
            return { valid: true, data: response };
        }
        
        // Check expiration
        if (Date.now() > response.goodUntil) {
            console.warn('[WebAPIClient] Signed response has expired');
            return { valid: false, data: null, error: 'Response signature expired' };
        }
        
        // If we have a signature secret, verify the signature
        if (this.signatureSecret) {
            const isValid = this.verifyHmacSignature(response);
            if (!isValid) {
                console.error('[WebAPIClient] Invalid response signature detected!');
                return { valid: false, data: null, error: 'Invalid response signature' };
            }
        }
        
        // Return the verified data
        return { valid: true, data: response.data };
    }
    
    /**
     * Verify HMAC signature (requires crypto-js or similar)
     * Note: In browser, we use SubtleCrypto API
     * @param {Object} signedResponse 
     * @returns {boolean}
     */
    async verifyHmacSignature(signedResponse) {
        if (!this.signatureSecret) {
            return true; // No secret configured, skip verification
        }
        
        try {
            const { data, userId, sessionId, goodUntil, signature } = signedResponse;
            
            // Recreate the payload that was signed
            const payloadString = JSON.stringify({
                data,
                userId,
                sessionId,
                goodUntil
            });
            
            // Use SubtleCrypto for HMAC verification in browser
            const encoder = new TextEncoder();
            const keyData = encoder.encode(this.signatureSecret);
            const messageData = encoder.encode(payloadString);
            
            const key = await crypto.subtle.importKey(
                'raw',
                keyData,
                { name: 'HMAC', hash: 'SHA-256' },
                false,
                ['sign']
            );
            
            const signatureBuffer = await crypto.subtle.sign('HMAC', key, messageData);
            const expectedSignature = Array.from(new Uint8Array(signatureBuffer))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
            
            return signature === expectedSignature;
        } catch (error) {
            console.error('[WebAPIClient] Signature verification error:', error);
            return false;
        }
    }
    
    /**
     * Extract data from a potentially signed response
     * Verifies signature if present and returns the inner data
     * @param {Object} response 
     * @param {boolean} requireValid - If true, throws on invalid signature
     * @returns {any} The data (unwrapped if signed)
     */
    extractSignedData(response, requireValid = true) {
        if (!this.verifySignatures) {
            // Signature verification disabled, extract data directly
            return response?.data !== undefined ? response.data : response;
        }
        
        const result = this.verifySignedResponse(response);
        
        if (!result.valid && requireValid) {
            throw new Error(result.error || 'Response verification failed');
        }
        
        return result.data;
    }
    
    // ==========================================
    // END-TO-END ENCRYPTION (CPI Architecture)
    // ==========================================
    
    /**
     * Initialize encrypted session with server
     * Uses RSA key exchange to establish AES session key
     * @returns {Object} { success, sessionId, error? }
     */
    async initializeEncryption() {
        try {
            // Generate RSA key pair for key exchange
            const keyPair = await crypto.subtle.generateKey(
                {
                    name: 'RSA-OAEP',
                    modulusLength: 2048,
                    publicExponent: new Uint8Array([1, 0, 1]),
                    hash: 'SHA-256'
                },
                true,
                ['encrypt', 'decrypt']
            );
            
            // Export public key to send to server
            const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
            const publicKeyBase64 = this.arrayBufferToBase64(publicKeyBuffer);
            
            // Send public key to server and receive encrypted AES key
            const response = await this.request('/game/encryption/init', {
                method: 'POST',
                body: { clientPublicKey: publicKeyBase64 }
            });
            
            if (!response.success) {
                throw new Error(response.error || 'Encryption initialization failed');
            }
            
            // Decrypt the AES key with our private key
            const encryptedKeyBuffer = this.base64ToArrayBuffer(response.encryptedKey);
            const aesKeyBuffer = await crypto.subtle.decrypt(
                { name: 'RSA-OAEP' },
                keyPair.privateKey,
                encryptedKeyBuffer
            );
            
            // Import the AES key
            this.aesKey = await crypto.subtle.importKey(
                'raw',
                aesKeyBuffer,
                { name: 'AES-GCM' },
                false,
                ['encrypt', 'decrypt']
            );
            
            this.encryptionSessionId = response.sessionId;
            this.aesKeyExpiry = response.expiresAt;
            this.encryptionEnabled = true;
            
            console.log('[WebAPIClient] Encryption initialized, session:', response.sessionId);
            
            return { success: true, sessionId: response.sessionId };
        } catch (error) {
            console.error('[WebAPIClient] Encryption initialization failed:', error);
            this.encryptionEnabled = false;
            return { success: false, error: error.message };
        }
    }
    
    /**
     * Rotate encryption key (call periodically for security)
     * @returns {Object} { success, error? }
     */
    async rotateEncryptionKey() {
        if (!this.encryptionEnabled || !this.encryptionSessionId) {
            return { success: false, error: 'Encryption not initialized' };
        }
        
        try {
            // Generate new RSA key pair
            const keyPair = await crypto.subtle.generateKey(
                {
                    name: 'RSA-OAEP',
                    modulusLength: 2048,
                    publicExponent: new Uint8Array([1, 0, 1]),
                    hash: 'SHA-256'
                },
                true,
                ['encrypt', 'decrypt']
            );
            
            const publicKeyBuffer = await crypto.subtle.exportKey('spki', keyPair.publicKey);
            const publicKeyBase64 = this.arrayBufferToBase64(publicKeyBuffer);
            
            const response = await this.request('/game/encryption/rotate', {
                method: 'POST',
                body: { 
                    sessionId: this.encryptionSessionId,
                    clientPublicKey: publicKeyBase64 
                }
            });
            
            if (!response.success) {
                throw new Error(response.error || 'Key rotation failed');
            }
            
            // Decrypt new AES key
            const encryptedKeyBuffer = this.base64ToArrayBuffer(response.encryptedKey);
            const aesKeyBuffer = await crypto.subtle.decrypt(
                { name: 'RSA-OAEP' },
                keyPair.privateKey,
                encryptedKeyBuffer
            );
            
            this.aesKey = await crypto.subtle.importKey(
                'raw',
                aesKeyBuffer,
                { name: 'AES-GCM' },
                false,
                ['encrypt', 'decrypt']
            );
            
            this.aesKeyExpiry = response.expiresAt;
            
            console.log('[WebAPIClient] Encryption key rotated');
            return { success: true };
        } catch (error) {
            console.error('[WebAPIClient] Key rotation failed:', error);
            return { success: false, error: error.message };
        }
    }
    
    /**
     * Encrypt data for transmission
     * @param {any} data - Data to encrypt
     * @returns {Object} { iv, data } - Encrypted payload
     */
    async encryptData(data) {
        if (!this.encryptionEnabled || !this.aesKey) {
            throw new Error('Encryption not initialized');
        }
        
        // Check if key needs rotation
        if (this.aesKeyExpiry && Date.now() > this.aesKeyExpiry - 60000) {
            await this.rotateEncryptionKey();
        }
        
        const encoder = new TextEncoder();
        const dataString = JSON.stringify(data);
        const dataBuffer = encoder.encode(dataString);
        
        // Generate random IV
        const iv = crypto.getRandomValues(new Uint8Array(12));
        
        // Encrypt with AES-GCM
        const encryptedBuffer = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            this.aesKey,
            dataBuffer
        );
        
        return {
            iv: this.arrayBufferToBase64(iv),
            data: this.arrayBufferToBase64(encryptedBuffer),
            sessionId: this.encryptionSessionId
        };
    }
    
    /**
     * Decrypt received data
     * @param {Object} payload - { iv, data } encrypted payload
     * @returns {any} Decrypted data
     */
    async decryptData(payload) {
        if (!this.encryptionEnabled || !this.aesKey) {
            throw new Error('Encryption not initialized');
        }
        
        const iv = this.base64ToArrayBuffer(payload.iv);
        const encryptedData = this.base64ToArrayBuffer(payload.data);
        
        const decryptedBuffer = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            this.aesKey,
            encryptedData
        );
        
        const decoder = new TextDecoder();
        const dataString = decoder.decode(decryptedBuffer);
        
        return JSON.parse(dataString);
    }
    
    /**
     * Send an encrypted message via WebAPI
     * For real-time encrypted communication relay
     * @param {any} data - Data to send encrypted
     * @returns {Object} Response with decrypted reply if applicable
     */
    async sendEncryptedMessage(data) {
        if (!this.encryptionEnabled) {
            await this.initializeEncryption();
        }
        
        const encrypted = await this.encryptData(data);
        
        const response = await this.request('/game/encryption/message', {
            method: 'POST',
            body: encrypted
        });
        
        // If response contains encrypted data, decrypt it
        if (response.encrypted && response.iv && response.data) {
            const decrypted = await this.decryptData(response);
            return { success: true, data: decrypted };
        }
        
        return response;
    }
    
    /**
     * Check if encryption is ready
     * @returns {boolean}
     */
    isEncryptionReady() {
        return this.encryptionEnabled && this.aesKey !== null;
    }
    
    /**
     * Get encryption status
     * @returns {Object} { enabled, sessionId, expiresAt }
     */
    getEncryptionStatus() {
        return {
            enabled: this.encryptionEnabled,
            sessionId: this.encryptionSessionId,
            expiresAt: this.aesKeyExpiry
        };
    }
    
    /**
     * Disable encryption (clear keys)
     */
    disableEncryption() {
        this.encryptionEnabled = false;
        this.encryptionSessionId = null;
        this.aesKey = null;
        this.aesKeyExpiry = null;
    }
    
    // Helper: ArrayBuffer to Base64
    arrayBufferToBase64(buffer) {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }
    
    // Helper: Base64 to ArrayBuffer
    base64ToArrayBuffer(base64) {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }
    
    // ==========================================
    // AUTHENTICATION ENDPOINTS
    // ==========================================
    
    /**
     * Login with username/email and password
     * NEW: May return penguin list for selection instead of direct login
     * @returns {Object} { success, requiresPenguinSelection, penguins, user, profile, sessionKey, error }
     */
    async login(username, password) {
        try {
            // Clear any existing tokens before attempting a new login
            // This prevents issues when switching accounts (e.g., login fails and 
            // old refresh token from different user gets used in retry loop)
            this.clearTokens();
            
            const response = await this.request('/auth/login', {
                method: 'POST',
                body: { username, password },
                skipAuth: true
            });
            
            // Check if penguin selection is required
            if (response.requiresPenguinSelection) {
                return {
                    success: true,
                    requiresPenguinSelection: true,
                    penguins: response.penguins || [],
                    userId: response.userId,
                    user: response.user,
                    message: response.message
                };
            }
            
            // Direct login (backward compatible or penguin auto-selected)
            this.accessToken = response.accessToken;
            this.refreshToken = response.refreshToken;
            this.sessionKey = response.sessionKey;
            this.profile = response.profile;
            
            this.storeTokens();
            
            return {
                success: true,
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                sessionKey: this.sessionKey,
                profile: this.profile
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Login failed'
            };
        }
    }
    
    /**
     * Select a penguin after authentication
     * @param {string} userId - Parent user ID
     * @param {string} penguinId - Selected penguin ID
     * @returns {Object} { success, accessToken, refreshToken, sessionKey, profile, error }
     */
    async selectPenguin(userId, penguinId) {
        try {
            const response = await this.request('/auth/select-penguin', {
                method: 'POST',
                body: { userId, penguinId },
                skipAuth: true
            });
            
            // Store tokens
            this.accessToken = response.accessToken;
            this.refreshToken = response.refreshToken;
            this.sessionKey = response.sessionKey;
            this.profile = response.profile;
            
            this.storeTokens();
            
            return {
                success: true,
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                sessionKey: this.sessionKey,
                profile: this.profile
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Penguin selection failed'
            };
        }
    }
    
    /**
     * Create a new penguin for the authenticated account
     * @param {string} userId - Parent user ID
     * @param {Object} penguinData - { username, displayName, color }
     * @returns {Object} { success, penguin, error }
     */
    async createPenguin(userId, penguinData) {
        try {
            const response = await this.request('/auth/create-penguin', {
                method: 'POST',
                body: { userId, ...penguinData },
                skipAuth: true
            });
            
            return {
                success: true,
                penguin: response.penguin
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Penguin creation failed'
            };
        }
    }
    
    /**
     * Register a new user
     * @returns {Object} { success, accessToken, refreshToken, sessionKey, profile, error }
     */
    async register(userData) {
        try {
            const response = await this.request('/auth/register', {
                method: 'POST',
                body: userData,
                skipAuth: true
            });
            
            // Store tokens
            this.accessToken = response.accessToken;
            this.refreshToken = response.refreshToken;
            this.sessionKey = response.sessionKey;
            this.profile = response.profile;
            
            this.storeTokens();
            
            return {
                success: true,
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                sessionKey: this.sessionKey,
                profile: this.profile
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Registration failed'
            };
        }
    }
    
    // ==========================================
    // NEW PENGUIN AUTH SYSTEM (January 2026)
    // ==========================================
    // These methods support the new architecture where:
    // - Parent accounts are created with email/password
    // - Penguin accounts have their own username/password
    // - Penguins are completely independent (separate inventory, coins, etc.)
    
    /**
     * Register a new parent/master account
     * STEP 1 of new registration flow
     * @param {Object} parentData - { email, password, isOver13, acceptedTerms, acceptedPrivacy }
     * @returns {Object} { success, parentId, email, error }
     */
    async registerParent(parentData) {
        try {
            const response = await this.request('/auth/penguin/register-parent', {
                method: 'POST',
                body: parentData,
                skipAuth: true
            });
            
            return {
                success: true,
                parentId: response.parentId,
                email: response.email
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Parent registration failed'
            };
        }
    }
    
    /**
     * Fetch a server-signed child CAPTCHA challenge.
     * Returns a signed token + display data (item positions).
     * Used for CHILD/TEEN ageBand instead of Google reCAPTCHA v3.
     */
    async childCaptchaChallenge() {
        try {
            const response = await this.request('/auth/captcha/child-challenge', {
                method: 'GET',
                skipAuth: true
            });
            return response; // { token, count, items: [{x, y}] }
        } catch (error) {
            return null;
        }
    }

    /**
     * Create a penguin under a parent account with penguin-specific password
     * STEP 2 of new registration flow
     * @param {Object} penguinData - { parentId, username, password, color, chatType, isUnder13, parentEmail }
     * @returns {Object} { success, penguin, requiresParentalConsent, error }
     */
    async createPenguinWithAuth(penguinData) {
        try {
            const response = await this.request('/auth/penguin/create', {
                method: 'POST',
                body: penguinData,
                skipAuth: true
            });
            
            // COPPA: Check if parental consent is required (202 response)
            if (response.requiresParentalConsent || response.status === 'pending_parental_consent') {
                return {
                    success: true, // Registration was successful, just pending consent
                    requiresParentalConsent: true,
                    pendingId: response.pendingId,
                    message: response.message,
                    parentEmailHint: response.parentEmailHint,
                    expiresAt: response.expiresAt,
                    emailDelivered: response.emailDelivered,
                    coppaNote: response.coppaNote
                };
            }
            
            return {
                success: true,
                penguin: response.penguin,
                consentVerified: response.consentVerified,
                parentEmailVerified: response.parentEmailVerified,
                message: response.message
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Penguin creation failed'
            };
        }
    }
    
    /**
     * Login with penguin credentials (username + penguin password)
     * This is the MAIN login endpoint for playing the game
     * @param {string} username - Penguin username
     * @param {string} password - Penguin password (NOT parent account password)
     * @returns {Object} { success, accessToken, refreshToken, sessionKey, profile, error }
     */
    async penguinLogin(username, password) {
        try {
            // Clear any existing tokens before attempting a new login
            this.clearTokens();
            
            const response = await this.request('/auth/penguin/login', {
                method: 'POST',
                body: { username, password },
                skipAuth: true
            });
            
            // Store tokens
            this.accessToken = response.accessToken;
            this.refreshToken = response.refreshToken;
            this.sessionKey = response.sessionKey;
            this.profile = response.profile;
            
            this.storeTokens();
            
            return {
                success: true,
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                sessionKey: this.sessionKey,
                profile: this.profile
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Login failed'
            };
        }
    }
    
    /**
     * Login to parent dashboard (email + parent password)
     * Returns list of penguins for management
     * @param {string} email - Parent account email
     * @param {string} password - Parent account password
     * @returns {Object} { success, parentAccount, penguins, sessionKey, error }
     */
    async parentDashboardLogin(email, password) {
        try {
            const response = await this.request('/auth/penguin/parent-login', {
                method: 'POST',
                body: { email, password },
                skipAuth: true
            });
            
            return {
                success: true,
                parentAccount: response.parentAccount,
                penguins: response.penguins,
                sessionKey: response.sessionKey
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Parent login failed'
            };
        }
    }
    
    /**
     * Get predefined messages for Ultimate Safe Chat
     * @returns {Object} { success, messages: { category: [...] }, error }
     */
    async getSafeMessages() {
        try {
            const response = await this.request('/auth/penguin/safe-messages', {
                method: 'GET',
                skipAuth: true
            });
            
            return {
                success: true,
                messages: response.messages
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to fetch safe messages'
            };
        }
    }
    
    /**
     * Check if a username is available
     * @param {string} username - The username to check
     * @returns {Object} { available, error }
     */
    async checkUsernameAvailability(username) {
        try {
            const response = await this.request(`/auth/check-username?username=${encodeURIComponent(username)}`, {
                method: 'GET',
                skipAuth: true // Public endpoint
            });
            
            return {
                available: response.available,
                error: response.error || null
            };
        } catch (error) {
            return {
                available: false,
                error: error.message || 'Could not check username availability'
            };
        }
    }
    
    /**
     * Update a penguin's username (for denied usernames)
     * @param {string} penguinId - The penguin ID
     * @param {string} newUsername - The new username to request
     * @returns {Object} { success, error }
     */
    async updateUsername(penguinId, newUsername) {
        try {
            const response = await this.request('/auth/update-username', {
                method: 'POST',
                body: { penguinId, newUsername }
            });
            
            return {
                success: true,
                message: response.message || 'Username update submitted'
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to update username'
            };
        }
    }
    
    /**
     * Refresh access token using refresh token
     * @returns {boolean} Whether refresh was successful
     */
    async refresh() {
        if (!this.refreshToken) return false;
        
        try {
            const response = await this.request('/auth/refresh', {
                method: 'POST',
                body: { refreshToken: this.refreshToken },
                skipAuth: true
            });
            
            this.accessToken = response.accessToken;
            this.storeTokens();
            
            return true;
        } catch (error) {
            // Refresh failed - clear tokens
            this.clearTokens();
            return false;
        }
    }
    
    /**
     * Login using refresh token (for "Remember my password" feature)
     * Creates a new session and returns full login response
     * @param {string} refreshToken - Stored refresh token
     * @returns {Object} { success, accessToken, refreshToken, sessionKey, profile, error }
     */
    async loginWithRefreshToken(refreshToken) {
        try {
            const response = await this.request('/auth/refresh-login', {
                method: 'POST',
                body: { refreshToken },
                skipAuth: true
            });
            
            // Store tokens
            this.accessToken = response.accessToken;
            this.refreshToken = response.refreshToken;
            this.sessionKey = response.sessionKey;
            this.profile = response.profile;
            
            this.storeTokens();
            
            return {
                success: true,
                accessToken: this.accessToken,
                refreshToken: this.refreshToken,
                sessionKey: this.sessionKey,
                profile: this.profile
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Session expired. Please login again.'
            };
        }
    }
    
    /**
     * Logout and invalidate session
     */
    async logout() {
        try {
            if (this.accessToken) {
                await this.request('/auth/logout', { method: 'POST' });
            }
        } catch (error) {
            console.warn('Logout request failed:', error);
        } finally {
            this.clearTokens();
        }
    }
    
    /**
     * Validate current token
     * @returns {Object} { valid, expiresAt, userId, sessionId }
     */
    async validate() {
        if (!this.accessToken) {
            return { valid: false };
        }
        
        try {
            return await this.request('/auth/validate');
        } catch (error) {
            return { valid: false };
        }
    }
    
    /**
     * Check if user is authenticated
     * @returns {boolean}
     */
    isAuthenticated() {
        return !!this.accessToken;
    }
    
    /**
     * Get credentials for game server connection
     * @returns {Object} { sessionKey, accessToken, username }
     */
    getGameCredentials() {
        if (!this.sessionKey || !this.accessToken || !this.profile) {
            return null;
        }
        
        return {
            sessionKey: this.sessionKey,
            accessToken: this.accessToken,
            username: this.profile.username
        };
    }
    
    /**
     * Get current user profile
     * @returns {Object|null}
     */
    getProfile() {
        return this.profile;
    }
    
    // ==========================================
    // PLAYER DATA ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get player's full profile data
     * @returns {Object} Player profile including coins, appearance, etc.
     */
    async getPlayerProfile() {
        try {
            return await this.request('/player/profile');
        } catch (error) {
            console.error('Failed to fetch player profile:', error);
            throw error;
        }
    }
    
    /**
     * Update player appearance
     * @param {Object} appearance - { color, head, face, neck, body, hand, feet, flag, photo }
     */
    async updateAppearance(appearance) {
        try {
            return await this.request('/player/appearance', {
                method: 'PUT',
                body: appearance
            });
        } catch (error) {
            console.error('Failed to update appearance:', error);
            throw error;
        }
    }
    
    /**
     * Get player's current coin balance
     */
    async getCoins() {
        try {
            const response = await this.request('/player/coins');
            return response.coins;
        } catch (error) {
            console.error('Failed to fetch coins:', error);
            throw error;
        }
    }
    
    // ==========================================
    // INVENTORY ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get player's clothing inventory
     * @returns {Array} List of clothing item IDs
     */
    async getInventory() {
        try {
            const response = await this.request('/player/inventory');
            return response.items || [];
        } catch (error) {
            console.error('Failed to fetch inventory:', error);
            throw error;
        }
    }
    
    /**
     * Purchase a clothing item
     * @param {number} itemId - Item to purchase
     * @returns {Object} { success, item, coins }
     */
    async purchaseItem(itemId) {
        try {
            return await this.request('/player/inventory', {
                method: 'POST',
                body: { itemId }
            });
        } catch (error) {
            console.error('Failed to purchase item:', error);
            throw error;
        }
    }
    
    /**
     * Get player's furniture inventory
     * @returns {Array} List of furniture with quantities
     */
    async getFurniture() {
        try {
            const response = await this.request('/player/furniture');
            return response.furniture || [];
        } catch (error) {
            console.error('Failed to fetch furniture:', error);
            throw error;
        }
    }
    
    /**
     * Purchase furniture
     * @param {number} furnitureId - Furniture to purchase
     * @param {number} quantity - Amount to purchase (default 1)
     * @returns {Object} { success, furnitureId, quantity, coins }
     */
    async purchaseFurniture(furnitureId, quantity = 1) {
        try {
            return await this.request('/player/furniture', {
                method: 'POST',
                body: { furnitureId, quantity }
            });
        } catch (error) {
            console.error('Failed to purchase furniture:', error);
            throw error;
        }
    }
    
    // ==========================================
    // IGLOO ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get player's igloo data
     * @returns {Object} Igloo configuration and furniture layout
     */
    async getIgloo() {
        try {
            return await this.request('/igloo');
        } catch (error) {
            console.error('Failed to fetch igloo:', error);
            throw error;
        }
    }
    
    /**
     * Update igloo furniture layout
     * @param {Array} furniture - Array of furniture placements
     */
    async updateIglooFurniture(furniture) {
        try {
            return await this.request('/igloo/furniture', {
                method: 'PUT',
                body: { furniture }
            });
        } catch (error) {
            console.error('Failed to update igloo furniture:', error);
            throw error;
        }
    }
    
    /**
     * Purchase an igloo
     * @param {number} iglooId - Igloo type to purchase
     * @returns {Object} { success, iglooId, coins }
     */
    async purchaseIgloo(iglooId) {
        try {
            return await this.request('/igloo/purchase', {
                method: 'POST',
                body: { iglooId }
            });
        } catch (error) {
            console.error('Failed to purchase igloo:', error);
            throw error;
        }
    }
    
    /**
     * Get owned igloos
     * @returns {Array} List of owned igloo IDs
     */
    async getOwnedIgloos() {
        try {
            const response = await this.request('/igloo/owned');
            return response.igloos || [];
        } catch (error) {
            console.error('Failed to fetch owned igloos:', error);
            throw error;
        }
    }
    
    /**
     * Update igloo type (change igloo structure)
     * @param {number} iglooType - Igloo type ID to change to
     * @returns {Object} { success }
     */
    async updateIglooType(iglooType) {
        try {
            return await this.request('/igloo/type', {
                method: 'PUT',
                body: { type: iglooType }
            });
        } catch (error) {
            console.error('Failed to update igloo type:', error);
            throw error;
        }
    }
    
    /**
     * Update igloo music
     * @param {number} musicId - Music track ID
     * @returns {Object} { success }
     */
    async updateIglooMusic(musicId) {
        try {
            return await this.request('/igloo/music', {
                method: 'PUT',
                body: { music: musicId }
            });
        } catch (error) {
            console.error('Failed to update igloo music:', error);
            throw error;
        }
    }
    
    /**
     * Update igloo flooring
     * @param {number} flooringId - Flooring ID
     * @returns {Object} { success, coins }
     */
    async updateIglooFlooring(flooringId) {
        try {
            return await this.request('/igloo/flooring', {
                method: 'PUT',
                body: { flooring: flooringId }
            });
        } catch (error) {
            console.error('Failed to update igloo flooring:', error);
            throw error;
        }
    }
    
    // ==========================================
    // SOCIAL ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get buddy list
     * @returns {Array} List of buddies with online status
     */
    async getBuddies() {
        try {
            const response = await this.request('/social/buddies');
            return response.buddies || [];
        } catch (error) {
            console.error('Failed to fetch buddies:', error);
            throw error;
        }
    }
    
    /**
     * Get pending buddy requests
     * @returns {Array} List of pending requests
     */
    async getBuddyRequests() {
        try {
            const response = await this.request('/social/buddy/requests');
            return response.requests || [];
        } catch (error) {
            console.error('Failed to fetch buddy requests:', error);
            throw error;
        }
    }
    
    /**
     * Send a buddy request
     * @param {string} targetId - User ID to send request to
     */
    async sendBuddyRequest(targetId) {
        try {
            return await this.request('/social/buddy/request', {
                method: 'POST',
                body: { targetId }
            });
        } catch (error) {
            console.error('Failed to send buddy request:', error);
            throw error;
        }
    }
    
    /**
     * Accept a buddy request
     * @param {string} requesterId - User ID of requester
     */
    async acceptBuddyRequest(requesterId) {
        try {
            return await this.request('/social/buddy/accept', {
                method: 'POST',
                body: { requesterId }
            });
        } catch (error) {
            console.error('Failed to accept buddy request:', error);
            throw error;
        }
    }
    
    /**
     * Reject a buddy request
     * @param {string} requesterId - User ID of requester
     */
    async rejectBuddyRequest(requesterId) {
        try {
            return await this.request('/social/buddy/reject', {
                method: 'POST',
                body: { requesterId }
            });
        } catch (error) {
            console.error('Failed to reject buddy request:', error);
            throw error;
        }
    }
    
    /**
     * Remove a buddy
     * @param {string} buddyId - User ID to remove
     */
    async removeBuddy(buddyId) {
        try {
            return await this.request('/social/buddy/remove', {
                method: 'POST',
                body: { buddyId }
            });
        } catch (error) {
            console.error('Failed to remove buddy:', error);
            throw error;
        }
    }
    
    /**
     * Get ignore list
     * @returns {Array} List of ignored user IDs
     */
    async getIgnoreList() {
        try {
            const response = await this.request('/social/ignores');
            return response.ignores || [];
        } catch (error) {
            console.error('Failed to fetch ignore list:', error);
            throw error;
        }
    }
    
    /**
     * Add user to ignore list
     * @param {string} userId - User ID to ignore
     */
    async addIgnore(userId) {
        try {
            return await this.request('/social/ignore', {
                method: 'POST',
                body: { userId }
            });
        } catch (error) {
            console.error('Failed to add ignore:', error);
            throw error;
        }
    }
    
    /**
     * Remove user from ignore list
     * @param {string} userId - User ID to unignore
     */
    async removeIgnore(userId) {
        try {
            return await this.request('/social/ignore', {
                method: 'DELETE',
                body: { userId }
            });
        } catch (error) {
            console.error('Failed to remove ignore:', error);
            throw error;
        }
    }
    
    // ==========================================
    // STAMPS ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get player's stamps collection
     * @returns {Array} List of earned stamp IDs
     */
    async getStamps() {
        try {
            const response = await this.request('/stamps');
            return response.stamps || [];
        } catch (error) {
            console.error('Failed to fetch stamps:', error);
            throw error;
        }
    }
    
    /**
     * Get complete stampbook data (stamps, appearance, cover, inventory)
     * @returns {Object} Complete stampbook data including stamps, appearance, cover layout, etc.
     */
    async getStampbookData() {
        try {
            return await this.request('/stamps/book');
        } catch (error) {
            console.error('Failed to fetch stampbook data:', error);
            throw error;
        }
    }
    
    /**
     * Get stamp book configuration
     * @returns {Object} Stamp book appearance settings
     */
    async getStampbook() {
        try {
            return await this.request('/stamps/book');
        } catch (error) {
            console.error('Failed to fetch stampbook:', error);
            throw error;
        }
    }
    
    /**
     * Update stamp book appearance
     * @param {Object} appearance - { colour, clasp, highlight, pattern }
     */
    async updateStampbookAppearance(appearance) {
        try {
            return await this.request('/stamps/book', {
                method: 'PUT',
                body: appearance
            });
        } catch (error) {
            console.error('Failed to update stampbook appearance:', error);
            throw error;
        }
    }
    
    /**
     * Update stamp book cover layout
     * @param {Array} cover - Array of stamp placements on cover
     */
    async updateStampbookCover(cover) {
        try {
            return await this.request('/stamps/cover', {
                method: 'PUT',
                body: { cover }
            });
        } catch (error) {
            console.error('Failed to update stampbook cover:', error);
            throw error;
        }
    }
    
    /**
     * Earn/collect a stamp
     * @param {number} stampId - Stamp ID to earn
     * @returns {Object} { success, stamp }
     */
    async earnStamp(stampId) {
        try {
            return await this.request(`/stamps/${stampId}`, {
                method: 'POST'
            });
        } catch (error) {
            console.error('Failed to earn stamp:', error);
            throw error;
        }
    }
    
    /**
     * Get another player's stamps collection
     * @param {string|number} playerId - Player ID to look up
     * @returns {Object} { stamps, stampbook, coverStamps, ... }
     */
    async getPlayerStamps(playerId) {
        try {
            return await this.request(`/stamps/player/${playerId}`);
        } catch (error) {
            console.error('Failed to fetch player stamps:', error);
            throw error;
        }
    }
    
    // ==========================================
    // PASSPORT BADGES ENDPOINTS
    // ==========================================
    
    /**
     * Get player's passport badges (3 featured stamps)
     * @returns {Object} { badges: [...stampIds] }
     */
    async getPassportBadges() {
        try {
            return await this.request('/passport-badges');
        } catch (error) {
            console.error('Failed to fetch passport badges:', error);
            throw error;
        }
    }
    
    /**
     * Set passport badges (replace all)
     * @param {Array} badges - Array of up to 3 stamp IDs
     * @returns {Object} { success, badges }
     */
    async setPassportBadges(badges) {
        try {
            return await this.request('/passport-badges', {
                method: 'PUT',
                body: { badges }
            });
        } catch (error) {
            console.error('Failed to set passport badges:', error);
            throw error;
        }
    }
    
    // ==========================================
    // PLAYER CARD / PUBLIC PROFILE
    // ==========================================
    
    /**
     * Get another player's public card data
     * @param {string} playerId - Player ID to look up
     * @returns {Object} Public player card data
     */
    async getPlayerCard(playerId) {
        try {
            return await this.request(`/player/${playerId}/card`);
        } catch (error) {
            console.error('Failed to fetch player card:', error);
            throw error;
        }
    }
    
    // ==========================================
    // MAIL/POSTCARD ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get player's inbox (received mail)
     * @returns {Object} { success, data: [...mail], unreadCount }
     */
    async getMail() {
        try {
            return await this.request('/mail');
        } catch (error) {
            console.error('Failed to fetch mail:', error);
            throw error;
        }
    }
    
    /**
     * Get sent mail history
     * @returns {Object} { success, data: [...mail] }
     */
    async getSentMail() {
        try {
            return await this.request('/mail/sent');
        } catch (error) {
            console.error('Failed to fetch sent mail:', error);
            throw error;
        }
    }
    
    /**
     * Send a postcard to another player
     * @param {string|number} recipientId - Recipient's player ID
     * @param {number} postcardId - Postcard type ID
     * @param {Object} details - Additional details (message, etc.)
     * @returns {Object} { success, data: { id, sentDate }, coins }
     */
    async sendMail(recipientId, postcardId, details = {}) {
        try {
            return await this.request('/mail', {
                method: 'POST',
                body: { recipientId, postcardId, details }
            });
        } catch (error) {
            console.error('Failed to send mail:', error);
            throw error;
        }
    }
    
    /**
     * Mark mail as read
     * @param {string|number} mailId - Mail ID to mark as read
     * @returns {Object} { success }
     */
    async markMailRead(mailId) {
        try {
            return await this.request(`/mail/${mailId}/read`, {
                method: 'POST'
            });
        } catch (error) {
            console.error('Failed to mark mail as read:', error);
            throw error;
        }
    }
    
    /**
     * Delete a mail item
     * @param {string|number} mailId - Mail ID to delete
     * @returns {Object} { success }
     */
    async deleteMail(mailId) {
        try {
            return await this.request(`/mail/${mailId}`, {
                method: 'DELETE'
            });
        } catch (error) {
            console.error('Failed to delete mail:', error);
            throw error;
        }
    }
    
    /**
     * Delete all mail (clear inbox)
     * @returns {Object} { success }
     */
    async clearMail() {
        try {
            return await this.request('/mail', {
                method: 'DELETE'
            });
        } catch (error) {
            console.error('Failed to clear mail:', error);
            throw error;
        }
    }
    
    /**
     * Get unread mail count
     * @returns {Object} { success, count }
     */
    async getUnreadMailCount() {
        try {
            return await this.request('/mail/unread/count');
        } catch (error) {
            console.error('Failed to get unread mail count:', error);
            throw error;
        }
    }
    
    // ==========================================
    // REWARD PACK ENDPOINTS (CPI Architecture)
    // ==========================================
    
    /**
     * Get eligible reward packs for the current penguin
     * @returns {Object} { success, packs: [...] }
     */
    async getEligibleRewardPacks() {
        try {
            return await this.request('/reward-packs/eligible');
        } catch (error) {
            console.error('Failed to fetch eligible reward packs:', error);
            throw error;
        }
    }
    
    /**
     * Claim a reward pack
     * @param {string} packId - Reward pack ID to claim
     * @returns {Object} { success, claimedItems, coins }
     */
    async claimRewardPack(packId) {
        try {
            return await this.request(`/reward-packs/${packId}/claim`, {
                method: 'POST'
            });
        } catch (error) {
            console.error('Failed to claim reward pack:', error);
            throw error;
        }
    }
    
    /**
     * Check if there are any unclaimed reward packs
     * @returns {Object} { success, hasUnclaimed, count }
     */
    async checkRewardPacks() {
        try {
            return await this.request('/reward-packs/check');
        } catch (error) {
            console.error('Failed to check reward packs:', error);
            throw error;
        }
    }
}
